import { useState } from 'react';
import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { FactorySelector } from '@/components/features/factory/FactorySelector';
import { TimezoneWidget } from '@/components/features/timezone/TimezoneWidget';
import { LanguageSwitcher } from '@/components/features/language/LanguageSwitcher';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { SidebarNavContent } from './sidebar-nav-content';

/**
 * Hamburger trigger + off-canvas drawer for viewports below `md`, where the
 * desktop sidebar rail (see DashboardLayout) is hidden. Also carries the
 * factory/timezone/language/theme controls that don't fit in the compact
 * mobile header, so they stay reachable rather than disappearing on mobile.
 */
export function MobileNav(): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 md:hidden"
          aria-label={t('navigation.open_menu')}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-4/5 max-w-xs p-0">
        <SheetTitle className="sr-only">{t('navigation.menu')}</SheetTitle>
        <div className="flex flex-wrap items-center gap-2 border-b px-3 py-3">
          <FactorySelector />
          <TimezoneWidget />
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
        <div className="flex-1 overflow-hidden">
          <SidebarNavContent isCollapsed={false} onNavigate={() => setOpen(false)} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
